// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - PWA Install Prompt Component
// ═══════════════════════════════════════════════════════════════════════════
// Smart install banner that appears after 30 seconds of usage
// User can dismiss with "Later" (7 days) or "Never show again"
// ═══════════════════════════════════════════════════════════════════════════

import { icons } from './icons.js';

// ─────────────────────────────────────────────────────────────────────────
// STATE
// ─────────────────────────────────────────────────────────────────────────

let deferredPrompt = null;
let bannerElement = null;
let timeoutId = null;
const TRIGGER_DELAY_MS = 30000; // 30 seconds
const LATER_DELAY_DAYS = 7;
const STORAGE_KEY = 'writtn_install_prompt';

// ─────────────────────────────────────────────────────────────────────────
// INITIALIZATION
// ─────────────────────────────────────────────────────────────────────────

export function initInstallPrompt() {
    // Capture the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        deferredPrompt = e;
        console.log('Install prompt captured');

        // Check if we should show the banner
        if (shouldShowBanner()) {
            scheduleShowBanner();
        }
    });

    // Track if app was installed
    window.addEventListener('appinstalled', () => {
        console.log('PWA was installed');
        deferredPrompt = null;
        hideBanner();
        setDismissed('installed');
    });
}

// ─────────────────────────────────────────────────────────────────────────
// BANNER DISPLAY
// ─────────────────────────────────────────────────────────────────────────

function scheduleShowBanner() {
    if (timeoutId) {
        clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
        if (shouldShowBanner() && deferredPrompt) {
            showBanner();
        }
    }, TRIGGER_DELAY_MS);
}

function showBanner() {
    if (bannerElement) return;

    bannerElement = document.createElement('div');
    bannerElement.className = 'install-banner';
    bannerElement.innerHTML = `
        <div class="install-banner-content">
            <div class="install-banner-icon">
                ${icons.logo}
            </div>
            <div class="install-banner-text">
                <span class="install-banner-title">Install Writtn</span>
                <span class="install-banner-desc">Add to your home screen for the best experience</span>
            </div>
        </div>
        <div class="install-banner-actions">
            <button class="install-banner-later" id="install-later">Later</button>
            <button class="install-banner-install" id="install-now">Install</button>
        </div>
        <button class="install-banner-close" id="install-close" aria-label="Close">
            ${icons.x}
        </button>
    `;

    document.body.appendChild(bannerElement);

    // Animate in
    requestAnimationFrame(() => {
        bannerElement.classList.add('active');
    });

    // Setup handlers
    document.getElementById('install-now').addEventListener('click', handleInstall);
    document.getElementById('install-later').addEventListener('click', () => handleDismiss('later'));
    document.getElementById('install-close').addEventListener('click', () => handleDismiss('never'));
}

function hideBanner() {
    if (!bannerElement) return;

    bannerElement.classList.remove('active');
    setTimeout(() => {
        bannerElement?.remove();
        bannerElement = null;
    }, 300);
}

// ─────────────────────────────────────────────────────────────────────────
// HANDLERS
// ─────────────────────────────────────────────────────────────────────────

async function handleInstall() {
    if (!deferredPrompt) {
        console.log('No deferred prompt available');
        return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for user choice
    const { outcome } = await deferredPrompt.userChoice;
    console.log('Install prompt outcome:', outcome);

    if (outcome === 'accepted') {
        setDismissed('installed');
    }

    deferredPrompt = null;
    hideBanner();
}

function handleDismiss(type) {
    if (type === 'later') {
        // Show again in 7 days
        const showAgainAt = Date.now() + (LATER_DELAY_DAYS * 24 * 60 * 60 * 1000);
        setDismissed('later', showAgainAt);
    } else {
        // Never show again
        setDismissed('never');
    }
    hideBanner();
}

// ─────────────────────────────────────────────────────────────────────────
// STORAGE
// ─────────────────────────────────────────────────────────────────────────

function shouldShowBanner() {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (!data) return true;

        const parsed = JSON.parse(data);

        if (parsed.status === 'never' || parsed.status === 'installed') {
            return false;
        }

        if (parsed.status === 'later' && parsed.showAgainAt) {
            return Date.now() >= parsed.showAgainAt;
        }

        return true;
    } catch {
        return true;
    }
}

function setDismissed(status, showAgainAt = null) {
    try {
        const data = { status, dismissedAt: new Date().toISOString() };
        if (showAgainAt) {
            data.showAgainAt = showAgainAt;
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.warn('Failed to save install prompt state:', e);
    }
}

// ─────────────────────────────────────────────────────────────────────────
// PUBLIC API (for Settings button)
// ─────────────────────────────────────────────────────────────────────────

export function isInstallable() {
    return deferredPrompt !== null;
}

export async function triggerInstall() {
    if (!deferredPrompt) {
        console.log('App is not installable or already installed');
        return false;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
        setDismissed('installed');
        deferredPrompt = null;
        return true;
    }

    return false;
}

export function getInstallStatus() {
    // Check if running as installed PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
        return 'installed';
    }
    if (window.navigator.standalone === true) {
        return 'installed'; // iOS Safari
    }
    if (deferredPrompt) {
        return 'installable';
    }
    return 'not-available';
}

export default {
    initInstallPrompt,
    isInstallable,
    triggerInstall,
    getInstallStatus,
};
